import * as React from "react";
import { ServiceDataModel } from "../../services/models/Models";
import { DataAddInStateManager } from "../../helpers/common/DataAddInStateManager";
import { ExcelHelper } from "../../helpers/ExcelHelper";
import styled from "styled-components";
import _ = require("lodash");
import {
  CellIndexMapping,
  Constants,
  CommonColumn,
  ExcelColorValue,
  general,
  UploadStatus,
  CommonConfiguration,
  ColumnType,
  ExcelFormatting,
} from "../../util/UIConstants";
import { EntityConfiguration } from "../../configurations/configuration";
import { debounce, getFormattedDate, labels } from "../../util/utils";
import { ServerSyncInfoSettings } from "../../helpers/common/ServerSyncInfoSettings";
import ErrorSummary from "./error/ErrorSummary";
import ProgressLoader from "./progressLoader";
import Utility from "../../util/utilityFunctions";
import { Environment } from "../../util";
import MotifButton from "@ey/motif-react-wrapper/Button";
import AtlasSummary from "./AtlasSummary";
import ErrorMessage from "./error/ErrorMessage";
import { ExcelUtilityHelper } from "../../helpers/common/ExcelUtilityHelper";
import SelectDropdownLayout from "./SelectDropdownLayout";
import { getBatchSize, referenceSheetExists } from "../../helpers/common/DataUtilityHelper";
import { TemplateService } from "../../services/template/TemplateService";

type addressRanges = "StartRange" | "EndRange";
type masterDataNames = "YesNoList";
type masterDataMetaDataValues = {
  keyColumn: string;
  keyAddressRange: string;
  value1Column: string;
  value1AddressRange: string;
  value2Column: string;
  value2AddressRange: string;
};
type masterDataDropDownMetaDataAddrRanges = Record<masterDataNames, masterDataMetaDataValues>;

function ExcelTableRenderer(props: any) {
  type errorInfo = { message: string; canShowError: boolean };
  type progressInfo = { message: string; canShowProgress: boolean };

  const [getProgressInfo, setProgressInfo] = React.useState<progressInfo>({ message: "", canShowProgress: false });
  const [getErrorInfo, setErrorInfo] = React.useState<errorInfo>({ message: "", canShowError: false });
  const [getUploadedOn, setUploadedOn] = React.useState<string>(labels.noUpdatesSaved);
  const [isUpdateInProgress, setIsUpdateInProgress] = React.useState<boolean>(false);
  const isAPICallRequired = React.useRef(true); //To decide if API call is required or not for update.
  const APIBatchProcessingCounter = React.useRef(0);
  const [selectedCellAddress, setSelectedCellAddress] = React.useState<any>(null);

  const UploadStatusCellName = props.configuration.getAttributeExcelCellName(
    props.configuration.columns,
    "UploadStatus"
  );
  const UploadStatusIdCellName = props.configuration.getAttributeExcelCellName(
    props.configuration.columns,
    "UploadStatusId"
  );
  const MarkForUpdateCellName = props.configuration.getAttributeExcelCellName(
    props.configuration.columns,
    ColumnType.MarkForUpdate
  );
  const EndingDataColumnCellName = props.configuration.getEndingDataExcelCellName(props.configuration.columns);
  const tableRowHeaderIndex = 2;

  //To decide to load new sheet or to reload already populated sheet
  React.useEffect(() => {
    // https://stackoverflow.com/a/33613918
    if (!props.loadExisting) {
      setProgressInfo({ message: labels.excelPopulatingMessage, canShowProgress: true });
      setTimeout(renderNow, 0);
    } else {
      setProgressInfo({ message: labels.offlineModeLoadingMessage, canShowProgress: true });
      setTimeout(loadExisting, 0);
    }

    // clean up effects function
    return () => {
      setTimeout(doCleanUp, 0);
    };
  }, []);

  const doCleanUp = async () => {
    await cleanUpAndRegisterEventSubscription(props.engagementIdToRender, true);
  };

  const handleCellSelectionChange = async (event: any) => {
    setSelectedCellAddress(event?.address);
    console.log("Selection changed to: " + event);
  };

  /**
   * Cleans up table and registers event subscription
   * @param {engagementId} - Engagement Id.
   * @param {doOnlyCleanUp} - to only cleanup or not.
   * Cleans up event handler requestTableDataCellsOnChange
   **/
  const cleanUpAndRegisterEventSubscription = debounce(async (engagementId: string, doOnlyCleanup: boolean = false) => {
    let cleanupSuccessful = false;
    await ExcelHelper.tryCatchRunAsync(async (context) => {
      let sheet = context.workbook.worksheets.getItem(props.activeSheetId);
      const tableName: string = `${Constants.RequestTablePrefix}${engagementId}`;
      let existingTable: Excel.Table = sheet.tables.getItemOrNullObject(tableName);
      await ExcelHelper.tryCatchContextSync(context, `get to register events ${tableName}`);

      if (!existingTable.isNullObject) {
        existingTable.onChanged.remove(requestTableDataCellsOnChange);
        sheet.onSelectionChanged.remove(handleCellSelectionChange);
        // multi-select-comobo: uncomment
        // releaseTableBindings(tableName);
        await ExcelHelper.tryCatchContextSync(context, `unsubscribe events ${tableName}`);
        cleanupSuccessful = true;
        if (!doOnlyCleanup) {
          existingTable.onChanged.add(requestTableDataCellsOnChange);
          sheet.onSelectionChanged.add(handleCellSelectionChange);
          // multi-select-comobo: uncomment
          // addTableBindings(tableName);
          await ExcelHelper.tryCatchContextSync(context, `subscribe events ${tableName}`);
          console.log("A handler has been registered for the onChanged event");
        }
      }
    }, `cleanUpAndRegisterExistingEventSubscription excel run ${engagementId}, doOnlyCleanup: ${doOnlyCleanup}`);
  }, 4000);

  const dataChangeInCellOnMultiSelect = async (cellAddress: any) => {
    let columnUpdated = cellAddress.match(/[A-Za-z]+/)[0]; // Match the first occurrence of letter(s)
    let columnToUpdate = [];
    let dataToUpdateBackToCanvas = Utility.getHashValueColumnsToUpdate(props.configuration.columns, [
      ColumnType.MarkForUpdate,
    ]);

    dataToUpdateBackToCanvas.forEach((column) => {
      columnToUpdate.push(CellIndexMapping[column.CellDisplayIndex]);
    });
    if (!columnToUpdate.includes(columnUpdated)) {
      return;
    }

    setIsUpdateInProgress(false);
    props.callBackOnSaveToServer("");

    await ExcelHelper.tryCatchRunAsync(async (context) => {
      console.log("Handler for Hash value. Data changed address: " + cellAddress);
      let sheet = context.workbook.worksheets.getItem(props.activeSheetId);
      // prevents any event from beinf fired from the table
      context.runtime.enableEvents = false;
      await ExcelHelper.tryCatchContextSync(
        context,
        `disable events before completing table change event handler ${cellAddress}`
      );

      let updatedRange = sheet.getRange(cellAddress);
      updatedRange.load("rowIndex");
      await ExcelHelper.tryCatchContextSync(context, `load for rowIndex`);

      // Get first and last updated row index
      const firstUpdatedRowIndex: number = updatedRange.rowIndex + 1;

      // on the table event change, we are updating other values on the same table, so disabling the events to avoid loops
      context.runtime.enableEvents = false;
      await ExcelHelper.tryCatchContextSync(context, `disable events`);
      await ExcelUtilityHelper.doRelativeCellUpdatesAndMarkDirty(
        firstUpdatedRowIndex,
        firstUpdatedRowIndex,
        sheet,
        context,
        MarkForUpdateCellName,
        props.configuration,
        UploadStatusCellName,
        UploadStatusIdCellName,
        EndingDataColumnCellName
      );
    }, `mark for update ${cellAddress}`)
      .catch(function (error) {
        // setErrorInfo({ canShowError: true, message: `Error modifying the cell values:  ${event.address}.` });
        throw error;
      })
      .finally(async function () {
        await ExcelHelper.tryCatchRunAsync(async (context) => {
          context.runtime.enableEvents = true; // enable the event back
          await ExcelHelper.tryCatchContextSync(
            context,
            `enable events before completing table change event handler ${cellAddress}`
          );
        }, "enable events back");
      });
  };

  /**
   * Event handler for cell value change
   * @param event - Cell value change event
   */
  const requestTableDataCellsOnChange = async (event: Excel.TableChangedEventArgs) => {
    if (event.source == "Local" && !isUpdateInProgress) {
      setProgressInfo({ message: "", canShowProgress: true });
      await ExcelHelper.tryCatchRunAsync(async (context) => {
        console.log(
          "Handler for table onChanged event has been triggered. Data changed address: " + event.address,
          event
        );
        let sheet = context.workbook.worksheets.getItem(props.activeSheetId);
        // prevents any event from beinf fired from the table
        context.runtime.enableEvents = false;
        await ExcelHelper.tryCatchContextSync(
          context,
          `disable events before completing table change event handler ${event.address}`
        );

        let updatedRange: Excel.Range = event.getRange(context);
        updatedRange.load("rowIndex");
        await ExcelHelper.tryCatchContextSync(context, `load for rowIndex`);

        // Get first and last updated row index
        const firstUpdatedRowIndex: number = updatedRange.rowIndex + 1;
        const getLastCellRange: Excel.Range = updatedRange.getLastCell();
        getLastCellRange.load("rowIndex");
        await ExcelHelper.tryCatchContextSync(context, `load for getLastCell/rowIndex`);
        const lastUpdatedRowIndex: number = getLastCellRange.rowIndex + 1;

        // on the table event change, we are updating other values on the same table, so disabling the events to avoid loops
        context.runtime.enableEvents = false;
        await ExcelHelper.tryCatchContextSync(context, `disable events`);
        const splittedModifiedRanges: Record<addressRanges, string> = getSplitedAddressRange(event.address);
        const isSingleCellUpdate: boolean = splittedModifiedRanges.EndRange === undefined;
        if (isSingleCellUpdate) {
          const changeDetail: Excel.ChangedEventDetail = event.details;
          if (
            changeDetail.valueTypeBefore === changeDetail.valueTypeAfter &&
            changeDetail.valueBefore === changeDetail.valueAfter
          ) {
            return;
          }

          // Validate MarkForUpdate value
          let markColumn = props.configuration.columns.find((cl) => cl.CellId == ColumnType.MarkForUpdate);
          let markCellName = CellIndexMapping[markColumn.CellDisplayIndex];
          const markValue = await ExcelUtilityHelper.getRowValue(context, sheet, lastUpdatedRowIndex, markCellName);
          if (markValue !== "" && markValue !== Constants.MarkForUpdateYes && markValue !== Constants.MarkForUpdateNo) {
            const { uploadedRowRange, uploadStatusColumnRange, uploadStatusIdColumnRange, markCellRange } =
              getCellRanges(lastUpdatedRowIndex, sheet);
            const expMessage: string = labels.markValueInvalid;
            // Call the method
            ExcelUtilityHelper.validateUserInput(
              uploadStatusColumnRange,
              uploadStatusIdColumnRange,
              uploadedRowRange,
              markCellRange,
              expMessage,
              Constants.MarkForUpdateNo,
              ExcelColorValue.Red
            );
            return;
          }

          if (splittedModifiedRanges.StartRange.startsWith(MarkForUpdateCellName)) {
            await props.configuration.uiExcelInterface?.doChangesWhenChangingUpdateEYCanvas(
              context,
              sheet,
              firstUpdatedRowIndex,
              lastUpdatedRowIndex,
              props.configuration,
              EndingDataColumnCellName,
              MarkForUpdateCellName,
              UploadStatusCellName,
              UploadStatusIdCellName
            );

            setIsUpdateInProgress(false);
            props.callBackOnSaveToServer("");
            return;
          }
        } else if (
          splittedModifiedRanges.StartRange.startsWith(MarkForUpdateCellName) &&
          splittedModifiedRanges.EndRange.startsWith(MarkForUpdateCellName)
        ) {
          await props.configuration.uiExcelInterface?.doChangesWhenChangingUpdateEYCanvas(
            context,
            sheet,
            firstUpdatedRowIndex,
            lastUpdatedRowIndex,
            props.configuration,
            EndingDataColumnCellName,
            MarkForUpdateCellName,
            UploadStatusCellName,
            UploadStatusIdCellName
          );

          setIsUpdateInProgress(false);
          props.callBackOnSaveToServer("");
          return;
        }
        let columnUpdated = event.address.match(/[A-Za-z]+/)[0]; // Match the first occurrence of letter(s)
        //columnUpdated = columnUpdated ? columnUpdated[0] : null; // Return the matched column letter(s) or null if not found

        let columnToUpdate = [];
        let dataToUpdateBackToCanvas = Utility.getHashValueColumnsToUpdate(props.configuration.columns, [
          ColumnType.MarkForUpdate,
        ]);

        dataToUpdateBackToCanvas.forEach((column) => {
          columnToUpdate.push(CellIndexMapping[column.CellDisplayIndex]);
        });

        if (columnToUpdate.includes(columnUpdated)) {
          await ExcelUtilityHelper.doRelativeCellUpdatesAndMarkDirty(
            firstUpdatedRowIndex,
            lastUpdatedRowIndex,
            sheet,
            context,
            MarkForUpdateCellName,
            props.configuration,
            UploadStatusCellName,
            UploadStatusIdCellName,
            EndingDataColumnCellName
          );
          setIsUpdateInProgress(false);
          props.callBackOnSaveToServer("");
        }
      }, `mark for update ${event.address}`)
        .catch(function (error) {
          // setErrorInfo({ canShowError: true, message: `Error modifying the cell values:  ${event.address}.` });
          throw error;
        })
        .finally(async function () {
          setProgressInfo({ message: "", canShowProgress: false });
          await ExcelHelper.tryCatchRunAsync(async (context) => {
            context.runtime.enableEvents = true; // enable the event back
            await ExcelHelper.tryCatchContextSync(
              context,
              `enable events before completing table change event handler ${event.address}`
            );
          }, "enable events back");
        });
    }
  };

  //To get the range of the modified cell on change only if column "Update EY Canvas" has been changed with some unaccepted value.
  const getCellRanges = (
    uploadedRowRangeIndex: number,
    sheet: Excel.Worksheet
  ): {
    uploadedRowRange: Excel.Range;
    uploadStatusColumnRange: Excel.Range;
    uploadStatusIdColumnRange: Excel.Range;
    markCellRange: Excel.Range;
  } => {
    const uploadedRowRange: Excel.Range = sheet.getRange(
      `${CommonColumn.BeginningColumn}${uploadedRowRangeIndex}:${EndingDataColumnCellName}${uploadedRowRangeIndex}`
    );
    const uploadStatusColumnRange: Excel.Range = sheet.getRange(
      `${UploadStatusCellName}${uploadedRowRangeIndex}:${UploadStatusCellName}${uploadedRowRangeIndex}`
    );
    const uploadStatusIdColumnRange: Excel.Range = sheet.getRange(
      `${UploadStatusIdCellName}${uploadedRowRangeIndex}:${UploadStatusIdCellName}${uploadedRowRangeIndex}`
    );

    const markCellRange: Excel.Range = sheet.getRange(
      `${MarkForUpdateCellName}${uploadedRowRangeIndex}:${MarkForUpdateCellName}${uploadedRowRangeIndex}`
    );

    return {
      uploadedRowRange,
      uploadStatusColumnRange,
      uploadStatusIdColumnRange,
      markCellRange,
    };
  };

  //To enable excel default formatting again after data population.
  async function enableDefaultFormatting(endIndex: number) {
    await Excel.run(async (context) => {
      let activeSheet = context.workbook.worksheets.getItem(props.activeSheetId);
      props.configuration.columns.forEach((column) => {
        const cellName = CellIndexMapping[column.CellDisplayIndex];
        if (column.CellFormat.disableDefaultFormatting) {
          let taskNameRange = activeSheet.getRange(
            `${cellName}${CommonConfiguration.dataStartingRowIndex}:${cellName}${endIndex}`
          );
          taskNameRange.numberFormat = [[general]];
        }
      });
    });
  }

  //To get current active sheet Id from the worksheet.
  const getActiveSheetIdFromWorksheet = async () => {
    let activeSheetId: string = "";
    await Excel.run(async (context) => {
      context.runtime.enableEvents = false;
      let currentSheet: Excel.Worksheet;
      currentSheet = context.workbook.worksheets.getActiveWorksheet();
      currentSheet.load("name");
      currentSheet.load("id");
      await ExcelHelper.tryCatchContextSync(context, `load the sheet name to text box`);
      activeSheetId = currentSheet.id;
    });
    return activeSheetId;
  };

  /**
   * Calls table renderer and gets settings
   */
  const renderNow = async () => {
    try {
      const rowsDataArray: Array<any> = await getRowsDataArray(props.requestsModel);
      let [masterDataAddressRanges, additionalReferenceSheetBindingInformation]: [
        masterDataDropDownMetaDataAddrRanges,
        any[]
      ] = await ExcelUtilityHelper.masterDataRender(
        rowsDataArray,
        props.requestsModel,
        props.activeSheetId,
        props.configuration
      );

      await tableRender(
        rowsDataArray,
        props.requestsModel,
        masterDataAddressRanges,
        props.configuration,
        additionalReferenceSheetBindingInformation
      );

      const renderDateString: string = new Date().toLocaleString();
      await cleanUpAndRegisterEventSubscription(props.engagementIdToRender);

      // persist to settings for loadExisting add-in-not-loaded mode
      let serverSyncSettings = await DataAddInStateManager.getPersistedServerSyncInfo(
        props.activeSheetId,
        props.activeSheetName
      );
      if (serverSyncSettings !== null) {
        serverSyncSettings.requestsRenderedDate = renderDateString;
        await DataAddInStateManager.persistServerSyncInfoSettings(serverSyncSettings);
      }
      await enableDefaultFormatting(rowsDataArray.length);
      await inputSheetProtection(props.activeSheetId);
    } catch (e) {
      let errorMessage = ExcelUtilityHelper.excelErrorMessageMapping(e.code);
      setErrorInfo({ canShowError: true, message: errorMessage });
      throw e;
    } finally {
      setProgressInfo({ canShowProgress: false, message: "" });
    }
  };

  const loadExisting = async () => {
    try {
      await cleanUpAndRegisterEventSubscription(props.engagementIdToRender);
      let serverSyncSettings = await DataAddInStateManager.getPersistedServerSyncInfo(
        props.activeSheetId,
        props.activeSheetName
      );
      if (serverSyncSettings !== null) {
        if (props.runOfflineMode) {
          await prepareRenderingAfterAddInOffline();
        }
        if (serverSyncSettings.requestUploadedDate !== null) {
          setUploadedOn(getFormattedDate(serverSyncSettings.requestUploadedDate, props.userDateTimeFormat));
        }
      }

      await ExcelHelper.tryCatchRunAsync(async (context) => {
        const range = context.workbook.getSelectedRange();

        // Load the address the selected range
        range.load(["address"]);

        // Sync the context to retrieve the loaded properties
        await context.sync();

        let selectedCellAddress = range.address;
        selectedCellAddress = selectedCellAddress.replace(props.activeSheetName + "!", "");
        setSelectedCellAddress(selectedCellAddress);
      });
    } catch (e) {
      let errorMessage = ExcelUtilityHelper.excelErrorMessageMapping(e.code, true);
      setErrorInfo({ canShowError: true, message: errorMessage });
      throw e;
    } finally {
      //await enableWorkbookProtection(true);
      setProgressInfo({ canShowProgress: false, message: "" });
    }
  };

  // WHY store in array then push it to excel table
  // https://docs.microsoft.com/en-us/office/dev/add-ins/excel/performance#set-range-values-in-an-array
  /**
   * Renders the table.
   * @param rowsArray - Data in array
   * @param requestsModel - Data model
   * @param masterDataAddressRanges - Address ranges for updatable cells
   */
  const tableRender = async (
    rowsArray: Array<any>,
    requestsModel: ServiceDataModel,
    masterDataAddressRanges: masterDataDropDownMetaDataAddrRanges,
    configuration,
    additionalReferenceSheetBindingInformation: any[]
  ) => {
    if (requestsModel && rowsArray && rowsArray.length > 0) {
      await ExcelHelper.tryCatchRunAsync(async (context) => {
        var {
          tableName,
          existingRequestsTable,
          sheet,
        }: { tableName: string; existingRequestsTable: Excel.Table; sheet: Excel.Worksheet } =
          await ExcelUtilityHelper.loadRequestsWorksheetTable(context, requestsModel.engagementId, props.activeSheetId);

        await ExcelUtilityHelper.populateExcelRowHeader(context, props.configuration, sheet);

        await context.sync();

        if (!existingRequestsTable.isNullObject) {
          existingRequestsTable.delete();
          await ExcelHelper.tryCatchContextSync(context, `delete existing table ${tableName}`);
        }

        existingRequestsTable = sheet.tables.add(
          `${CommonColumn.BeginningColumn}${tableRowHeaderIndex}:${EndingDataColumnCellName}${tableRowHeaderIndex}`,
          true
        );
        existingRequestsTable.name = tableName;
        existingRequestsTable.getHeaderRowRange().values = [rowsArray[0]];
        if (rowsArray.length > 1) {
          ExcelUtilityHelper.defaultFormatRequestTableColors(
            sheet,
            CommonConfiguration.dataStartingRowIndex,
            rowsArray.length,
            props.configuration
          );
          let copiedArrayItems = [...rowsArray];
          let headerSeparatedRecords = copiedArrayItems.splice(1);
          let excelBulkLoadingBatchSize = Environment.getSettings().excelBulkLoadingBatchSize;
          if (excelBulkLoadingBatchSize) {
            let batchedItems = splitRecordsIntoBatches(headerSeparatedRecords, excelBulkLoadingBatchSize);
            //This is the recommended way of adding rows to table
            for (let i = 0; i < batchedItems.length; i++) {
              // Add multiple rows at once
              existingRequestsTable.rows.add(null, batchedItems[i]);
              await ExcelHelper.tryCatchContextSync(context, `load records ${tableName}`);
            }
          } else {
            let batchedItems =
              headerSeparatedRecords.length > 7500
                ? splitIntoBatches(headerSeparatedRecords, 5000)
                : [headerSeparatedRecords];

            for (let i = 0; i < batchedItems.length; i++) {
              for (let j: number = 0; j < batchedItems[i].length; j++) {
                existingRequestsTable.rows.add(null, [batchedItems[i][j]]);
              }
              await ExcelHelper.tryCatchContextSync(context, `load records ${tableName}`);
            }
          }
        }

        if (Office.context.requirements.isSetSupported("ExcelApi", "1.2")) {
          sheet.getUsedRange().format.autofitColumns();
          sheet.getUsedRange().format.autofitRows();
        }

        context.runtime.load("enableEvents");
        await context.sync();
        context.runtime.enableEvents = true;
        await context.sync();
        await ExcelHelper.tryCatchContextSync(context, `load records ${tableName}`);

        const masterSheetName = `${Constants.MasterWorkSheetPrefix}`;
        if (rowsArray.length > 1)
          await masterDataDropDownBinding(
            context,
            masterSheetName,
            masterDataAddressRanges,
            sheet,
            rowsArray.length,
            tableName,
            additionalReferenceSheetBindingInformation,
            configuration
          );

        await protectNonEditables(context, tableName, rowsArray.length, requestsModel);
        if (rowsArray.length == 1 && rowsArray.length < CommonConfiguration.dataStartingRowIndex) {
          await protectSheetNoRows(sheet, props.configuration);
          context.sync();
        }
      });
    }
  };

  /**
   *
   * @param dataModel - Data edit model
   * @returns Array
   */
  // Returns the formatted data array for the table (Excel table). Data will be different for each entity type.
  // Based on api response and columns to show in excel, it is creating Array of records to load into excel
  const getRowsDataArray = function async(dataModel: ServiceDataModel): Array<any> {
    // need to fix this make common same as template based
    const rowsArray = new Array<any>();
    // header row
    let rowData = new Array<any>();
    props.configuration.columns.forEach((column) => {
      rowData.push(column.CellHeader);
    });

    rowsArray.push(rowData);

    let data = props.configuration.uiExcelInterface.getMappedListData(
      dataModel,
      props.configuration,
      props.engagementIdToRender,
      rowsArray
    );

    return data;
  };

  /**
   * Update EY Canvas button click handler
   */
  const onUploadClick: React.MouseEventHandler<HTMLButtonElement> = async () => {
    // setProgressInfo({ message: labels.loadingContent, canShowProgress: true });
    let activeSheetId: string = await getActiveSheetIdFromWorksheet();
    let sheetName: "";
    let settings: ServerSyncInfoSettings = null;
    try {
      settings = await DataAddInStateManager.getPersistedServerSyncInfo(activeSheetId, sheetName);
    } catch {
      console.warn("Error loading existing settings, so skipped loading.");
    }
    if (settings != null) {
      //setIsUpdateInProgress(false);
      await uploadChanges().catch(function (error) {
        setErrorInfo({ canShowError: true, message: labels.generalError });
      });
    } else {
      await ExcelHelper.tryCatchRunAsync(async (context) => {
        let sheet = context.workbook.worksheets.getItemOrNullObject(props.activeSheetId);
        await context.sync();
        if (sheet.isNullObject) {
          setErrorInfo({ canShowError: true, message: labels.generalError });
        } else {
          sheet.activate();
        }
        context.runtime.enableEvents = true;
        setProgressInfo({ message: "", canShowProgress: false });
        await ExcelHelper.tryCatchContextSync(context, `enable events after upload`);
      }, "enable events after upload");
    }
    // }
    //})
  };

  const uploadRequestsToServer = async (
    modifedRequests: Array<any>,
    APIBatchProcessingCounter,
    updatedModifiedRequest
  ) => {
    let errorMessageText;
    if (!_.isEmpty(modifedRequests)) {
      if (!isAPICallRequired.current) {
        APIBatchProcessingCounter.current = 1;
        errorMessageText = labels.errorMessageOnExcelUpdate;
        return errorMessageText;
      }
      console.log("something to upload " + modifedRequests.length);
      errorMessageText = await props.configuration.apiInterface.saveDataAsync(
        props.engagementIdToRender,
        modifedRequests,
        props.configuration,
        APIBatchProcessingCounter,
        updatedModifiedRequest
      );

      console.log("Done uploading " + modifedRequests.length + "requests to server");
    }

    return errorMessageText;
  };

  // To update UI as per response
  async function renderUIandResetFilters(
    context: Excel.RequestContext,
    sheet: Excel.Worksheet,
    existingRequestsTable: Excel.Table,
    batch: any,
    referenceSheetData: any,
    cleanAndEnable: (renderUICompleted) => void,
    renderUICompleted = false
  ) {
    let recalculateHashRowIndexs = await props.configuration.uiExcelInterface.updateWorksheetAfterUpload(
      context,
      sheet,
      existingRequestsTable,
      batch, // batchedItems
      props.configuration,
      referenceSheetData
    );

    await updateHashValues(recalculateHashRowIndexs);

    // Enable table events after upload and update are complete
    await cleanAndEnable(renderUICompleted);
  }

  /**
   * Fetches all the rows marked for update
   * Creates update model
   * batches update models and invokes API call method
   */
  async function updateHashValues(recalculatedRowIndexes: any): Promise<void> {
    await ExcelHelper.tryCatchRunAsync(async (context) => {
      await ExcelHelper.tryCatchContextSync(context, `disable events before upload`);
      let sheet = context.workbook.worksheets.getItem(props.activeSheetId);
      recalculatedRowIndexes?.forEach(async (rowIndex) => {
        await ExcelUtilityHelper.recalculateHashvalue(rowIndex, sheet, context, props.configuration);
      });
    });
  }

  /**
   * Fetches all the rows marked for update
   * Creates update model
   * batches update models and invokes API call method
   */
  async function uploadChanges(): Promise<void> {
    const engagementId: string = props.engagementIdToRender;
    props.callBackOnSaveToServer("");
    //setProgressInfo({ message: "Uploading changes to EY Canvas", canShowProgress: true });
    await ExcelHelper.tryCatchRunAsync(async (context) => {
      context.runtime.enableEvents = false;

      await ExcelHelper.tryCatchContextSync(context, `disable events before upload`);
      const { tableName, updatedTableRowIndexes, existingRequestsTable, sheet } = await getTableData(
        context,
        engagementId
      );
      if (updatedTableRowIndexes == null) {
        setIsUpdateInProgress(true);
        context.runtime.enableEvents = true;
        return;
      }
      setProgressInfo({ message: labels.updatingCanvas, canShowProgress: true });
      if (updatedTableRowIndexes !== null) {
        if (props.configuration?.referenceSheetColumns?.length) {
          var referenceSheetExist = await referenceSheetExists(props.activeSheetId);

          //check if ref sheet missing
          if (!referenceSheetExist) {
            setProgressInfo({ message: "", canShowProgress: false });
            context.runtime.enableEvents = true;
            setErrorInfo({ canShowError: true, message: labels.missingReferenceSheet });
            return;
          }
        }
        // proceed if refrence sheet present
        let referenceSheetData = null;

        let modifiedRequests: Array<any> = await prepareApiPayloadDataFromTable(
          context,
          existingRequestsTable,
          updatedTableRowIndexes,
          referenceSheetData
        );
        if (modifiedRequests.length == 0) {
          setProgressInfo({ message: "", canShowProgress: false });
          context.runtime.enableEvents = true;
          return;
        }

        //based on configuration type, we will set the appropriate batch size from AppConfig
        let batchSizeToSend = getBatchSize(props.configuration);
        if (props.configuration.entityConfiguration["apiTemplate"]?.bulkupdateApi == false) {
          batchSizeToSend = 1;
        }

        //This will only be used in case of patch update flow to create patch batch directly for batching
        let patchBatchOperations = null;

        if (
          props.configuration.entityConfiguration.apiTemplate?.updateMethodType == "PATCH" &&
          isAPICallRequired.current
        ) {
          // execute for Patch
          patchBatchOperations = TemplateService.getPatchInputBodyData(modifiedRequests, props.configuration);
        }
        let batchedItems = !isAPICallRequired.current
          ? [modifiedRequests]
          : splitIntoBatches(patchBatchOperations ? patchBatchOperations : modifiedRequests, batchSizeToSend);

        let cleanAndEnable = cleanUpAndEnableEvents(context, batchedItems.length, existingRequestsTable, sheet);

        APIBatchProcessingCounter.current = 0;

        let errorMessage = "";
        let startingIndexOfUdatedRow = 0;
        let skipMainAPICall = false;

        //In case of patch if there is property uploaded for main API call and just there is a update in payload for postprocessingAPI Calls such as tags then it will skip main API call and will proceed with postprocessingAPI call.
        if (modifiedRequests.length > 0 && batchedItems.length == 0) {
          batchedItems = [modifiedRequests];
          APIBatchProcessingCounter.current = batchedItems.length;
          skipMainAPICall = true;
        }

        const processBatch = async (batch: any) => {
          if (!skipMainAPICall) {
            try {
              let endIndexOfUdatedRow = batch[batch.length - 1].editRowIndex;
              let updatedModifiedRequest = modifiedRequests.filter(
                (request) =>
                  request.editRowIndex >= startingIndexOfUdatedRow && request.editRowIndex <= endIndexOfUdatedRow
              );
              startingIndexOfUdatedRow = endIndexOfUdatedRow;
              errorMessage = await uploadRequestsToServer(batch, APIBatchProcessingCounter, updatedModifiedRequest);
              await props.callBackOnSaveToServer(errorMessage);
            } catch (e) {
              console.log(e);
            }
          }

          if (!props.configuration.entityConfiguration["apiTemplate"]?.["additionalAPICallRequired"]) {
            let editedRequest = patchBatchOperations?.length ? modifiedRequests : batch;
            await renderUIandResetFilters(
              context,
              sheet,
              existingRequestsTable,
              editedRequest,
              referenceSheetData,
              cleanAndEnable
            );
          } else {
            if (APIBatchProcessingCounter.current == batchedItems.length) {
              let editedRequest = patchBatchOperations?.length ? modifiedRequests : batchedItems.flat(1);
              await props.configuration.apiInterface.postProcessingSteps(
                editedRequest,
                props.configuration,
                engagementId,
                () =>
                  renderUIandResetFilters(
                    context,
                    sheet,
                    existingRequestsTable,
                    editedRequest,
                    referenceSheetData,
                    cleanAndEnable,
                    true
                  ),
                props.callBackOnSaveToServer,
                errorMessage
              );
            }
          }
        };

        if (props.configuration.entityConfiguration["apiTemplate"]?.sequentialBatchProcessing) {
          for (const batch of batchedItems) {
            await processBatch(batch);
          }
        } else {
          batchedItems.forEach((batch: any) => processBatch(batch));
        }
      } else {
        setProgressInfo({ message: "", canShowProgress: false });
        context.runtime.enableEvents = true;
      }
    });
  }

  // To return if any index is marked for update.
  async function getTableData(context: Excel.RequestContext, engagementId: string) {
    // Get table name, table, sheet
    var {
      tableName,
      existingRequestsTable,
      sheet,
    }: { tableName: string; existingRequestsTable: Excel.Table; sheet: Excel.Worksheet } =
      await ExcelUtilityHelper.loadRequestsWorksheetTable(context, engagementId, props.activeSheetId);

    const updatedTableRowIndexes: number[] = await (
      await ExcelUtilityHelper.getTableBodyRowNumbersMarkedForUpdate(
        context,
        existingRequestsTable,
        props.configuration
      )
    ).updatedRowIndexes;

    return { tableName, updatedTableRowIndexes, existingRequestsTable, sheet };
  }

  const splitIntoBatches = (arr, size: number) => {
    const batches = [];
    for (let i = 0; i < arr.length; i += size) {
      batches.push(arr.slice(i, i + size));
    }
    return batches;
  };

  function splitRecordsIntoBatches<T>(arr: T[], size: number): T[][] {
    const batches: T[][] = [];
    for (let i = 0; i < arr.length; i += size) {
      batches.push(arr.slice(i, i + size));
    }
    return batches;
  }

  /**
   * Runs clean up and enables event handlers
   * @param context
   * @param batchItems
   * @returns - function and limits to a single invocation.
   */
  const cleanUpAndEnableEvents = (
    context: Excel.RequestContext,
    batchItems: number,
    existingRequestsTable: Excel.Table,
    sheet: Excel.Worksheet
  ): (() => void) => {
    let counter = 0;
    return async function (renderUICompleted = false) {
      if (counter >= batchItems - 1 || renderUICompleted) {
        //cleanUpAndRegisterEventSubscription(props.engagementIdToRender, true);
        context.runtime.enableEvents = true;
        if (isAPICallRequired.current) {
          const uploadedDate: Date = new Date();
          // persist to settings for loadExisting add-in-not-loaded mode
          let serverSyncSettings = await DataAddInStateManager.getPersistedServerSyncInfo(
            props.activeSheetId,
            props.activeSheetName
          );
          if (serverSyncSettings !== null) {
            serverSyncSettings.requestUploadedDate = uploadedDate;
            //serverSyncSettings.selectedEntityType = EntityType.Document;
            await DataAddInStateManager.persistServerSyncInfoSettings(serverSyncSettings);
          }
          setUploadedOn(getFormattedDate(uploadedDate, props.userDateTimeFormat));
        }

        /*if (Office.context.requirements.isSetSupported("ExcelApi", "1.2")) {
          sheet.getUsedRange().format.autofitColumns();
          sheet.getUsedRange().format.autofitRows();
        }*/

        await ExcelHelper.tryCatchContextSync(context, `updateWorksheetAfterUpload`);
        try {
          existingRequestsTable.reapplyFilters();
          // Get the count of visible rows after applying the filter
          const visibleRange = existingRequestsTable.getDataBodyRange().getVisibleView();
          visibleRange.load("rowCount");
          await context.sync();
          const visibleRowCount = visibleRange.rowCount;
          if (visibleRowCount <= 0) {
            existingRequestsTable.clearFilters();
            await context.sync();
          }
        } catch (e) {
          console.error(`Error in updateWorksheetAfterUpload: ${e}`);
        } finally {
          // To reset hidden columns after update complete
          resetHiddenColumns(sheet);
          setProgressInfo({ message: "", canShowProgress: false });
          await context.sync();
        }
      }
      counter++;
    };
  };

  //For indexes marked for update, It create Payload data for API.
  async function prepareApiPayloadDataFromTable(
    context: Excel.RequestContext,
    existingRequestsTable: Excel.Table,
    updatedTableRowIndexes: number[],
    data
  ): Promise<Array<any>> {
    // MIGHT BE AN ISSUE IN THE FUTURE, MIGHT DEMAND RANGE logic
    // Note that unlike ranges or columns, which will adjust if new rows or columns are added before them,
    //          a `TableRow` object represents the physical location of the table row, but not the data.
    //          That is, if the data is sorted or if new rows are added, a table row will continue
    //          to point at the index for which it was created.
    let updatedRows: Array<Excel.TableRow> = new Array<Excel.TableRow>();

    updatedTableRowIndexes.forEach((updatedRowIndex) => {
      let updatedRow: Excel.TableRow = existingRequestsTable.rows.getItemAt(updatedRowIndex);
      updatedRow.load("values, index");
      updatedRows.push(updatedRow);
    });
    let enableValidation = Environment.getSettings().enableUIValidation;
    isAPICallRequired.current = true;
    await ExcelHelper.tryCatchContextSync(context, "load the updated row values");
    let validatedData = await props.configuration.uiExcelInterface.validateInputData(
      updatedRows,
      props.configuration,
      enableValidation,
    );
    if (validatedData && validatedData.validationFail) {
      isAPICallRequired.current = !validatedData.validationFail;
    }
    return validatedData.modifiedRequests;
  }

  /**
   * Binds master table data to dropdown values
   * @param context - The Context.
   * @param masterSheetName - Master sheet name.
   * @param masterDataAddressRanges - Address ranges.
   * @param inputBoundSheet - The sheet.
   * @param noOfDataRows - Number of rows.
   * @param tableName - Table Name.
   */
  async function masterDataDropDownBinding(
    context: Excel.RequestContext,
    masterSheetName: string,
    masterDataAddressRanges: masterDataDropDownMetaDataAddrRanges,
    inputBoundSheet: Excel.Worksheet,
    noOfDataRows: number,
    tableName: string,
    additionalReferenceSheetBindingInformation: any[],
    configuration: any
  ) {
    let worksheets: Excel.WorksheetCollection = context.workbook.worksheets.load("items");
    let masterSheet: Excel.Worksheet = worksheets.getItemOrNullObject(masterSheetName);
    await ExcelHelper.tryCatchContextSync(context, `load the sheet for binding ${masterSheetName}`);
    if (configuration?.referenceSheetColumns?.length) {
      let markUpdateRangeMasterValueList = masterSheet.getRange(masterDataAddressRanges.YesNoList.value1AddressRange);
      let markUpdateColumnDataRangeValues: String = `${MarkForUpdateCellName}${
        CommonConfiguration.dataStartingRowIndex
      }:${MarkForUpdateCellName}${noOfDataRows + 1}`;
      let markUpdateColumnDataRange: Excel.Range = inputBoundSheet.getRange(`${markUpdateColumnDataRangeValues}`);
      markUpdateColumnDataRange.dataValidation.clear();
      markUpdateColumnDataRange.dataValidation.rule = {
        list: {
          inCellDropDown: true,
          source: markUpdateRangeMasterValueList,
        },
      };
    }

    if (props.configuration?.uiExcelInterface?.boundSheetDropDownBinding) {
      await props.configuration?.uiExcelInterface?.boundSheetDropDownBinding(
        masterSheet,
        context,
        noOfDataRows,
        additionalReferenceSheetBindingInformation,
        props.configuration,
        inputBoundSheet
      );
    }

    await ExcelHelper.tryCatchContextSync(context, `data bounded to excel cells ${tableName}`);
  }

  // enable, disable worksheet protection
  async function inputSheetProtection(sheetNameOrId: string) {
    await ExcelHelper.tryCatchRunAsync(async (context) => {
      await ExcelHelper.tryCatchContextSync(context, `load the workbook sheet: ${sheetNameOrId}`);
      let activeSheet = context.workbook.worksheets.getItem(sheetNameOrId);
      activeSheet.load("protection/protected");
      await ExcelHelper.tryCatchContextSync(context, `load the protection properties ${sheetNameOrId}`);

      const protectionOptions = {
        allowInsertRows: false,
        allowDeleteRows: false,
        allowEditObjects: true,
        allowAutoFilter: true,
        allowDeleteColumns: false,
        allowEditScenarios: true,
        allowFormatCells: true,
        allowFormatColumns: true,
        allowFormatRows: true,
        allowInsertHyperlinks: false,
        allowSort: true,
        allowPivotTables: false,
        allowInsertColumns: false,
        selectionMode: Excel.ProtectionSelectionMode.normal,
      };

      if (!activeSheet.protection.protected) {
        await ExcelUtilityHelper.ExcelSheetFormatting(
          context,
          props.activeSheetId,
          ExcelFormatting.passwordProtect,
          props.engagementIdToRender,
          protectionOptions
        );
      }

      await ExcelHelper.tryCatchContextSync(context, `protect input main worksheet ${sheetNameOrId}`);
    });
  }

  /**
   * Invoked when Addin comes online
   * Loads worksheet and table and finds rows marked for update
   * Updates Hash for marked rows
   * invokes coloring function
   */
  async function prepareRenderingAfterAddInOffline(): Promise<void> {
    // await loadTimePhaseMasterDataToPropsFromWorksheet(timePhaseMetaData);
    await ExcelHelper.tryCatchRunAsync(async (context) => {
      // on the table event change, we are updating other values on the same table, so disabling the events to avoid loops
      context.runtime.enableEvents = false;

      var {
        tableName,
        existingRequestsTable,
        sheet,
      }: { tableName: string; existingRequestsTable: Excel.Table; sheet: Excel.Worksheet } =
        await ExcelUtilityHelper.loadRequestsWorksheetTable(context, props.engagementIdToRender, props.activeSheetId);
      // Table Body Row Index means Table header excluded and Table Body zero-based index.
      var {
        updatedRowIndexes,
        notUpdatedRowIndexes,
        totalDataRowCount,
      }: { updatedRowIndexes: number[]; notUpdatedRowIndexes: number[]; totalDataRowCount: number } =
        await ExcelUtilityHelper.getTableBodyRowNumbersMarkedForUpdate(
          context,
          existingRequestsTable,
          props.configuration
        );

      // For manually edited fields with MarkForUpdate to true,
      // scans and updates hash values PLUS status =  pending, row color to yellow during offline
      if (updatedRowIndexes !== null && !_.isEmpty(updatedRowIndexes)) {
        setIsUpdateInProgress(false);
        props.callBackOnSaveToServer("");
        let updatedRowAddressIndex: Array<number> = new Array<number>(); // non-zero-based-header-included +1 +1
        let uploadStatusRanges: Array<Excel.Range> = [];
        let uploadStatusIdRanges: Array<Excel.Range> = [];

        for (const updatedRowIndex of updatedRowIndexes) {
          const statusRange: Excel.Range = sheet.getRange(
            `${UploadStatusCellName}${updatedRowIndex + 2}:${UploadStatusCellName}${updatedRowIndex + 2}`
          );
          uploadStatusRanges.push(statusRange);
          statusRange.load("values");
          await context.sync();
          if (String(statusRange.values) != "") {
            const statusIdRange: Excel.Range = sheet.getRange(
              `${UploadStatusIdCellName}${updatedRowIndex + 3}:${UploadStatusCellName}${updatedRowIndex + 3}`
            );
            uploadStatusIdRanges.push(statusIdRange);
            statusIdRange.load("values");
            updatedRowAddressIndex.push(updatedRowIndex + 3);
          }
        }
        await ExcelHelper.tryCatchContextSync(context, `load marked for update = true column values after offline`);
        await ExcelUtilityHelper.updateHashValues(updatedRowAddressIndex, sheet, context, props.configuration);

        ExcelUtilityHelper.updateStatusColumn(uploadStatusRanges, uploadStatusIdRanges, UploadStatus.Pending);
        await colorRowAfterOffline(context, updatedRowIndexes, sheet, totalDataRowCount + 1, ExcelColorValue.Yellow);

        await ExcelHelper.tryCatchContextSync(
          context,
          `update marked for update = true column to proper values after offline`
        );
      }

      // For manually edited rows with MarkForUpdate to false,
      // scans and updates hash values PLUS MarkForUpdate to True status =  pending, row color to yellow during offline
      // Leaves the last uploaded entries which are marked as uploaded in table
      if (!_.isEmpty(notUpdatedRowIndexes)) {
        let notUpdatedRowAddressIndex: Array<number> = new Array<number>(); // non-zero-based-header-included +1 +1
        let updatedRowAddressIndex: Array<number> = new Array<number>();
        let uploadStatusRanges: Array<Excel.Range> = [];
        let uploadStatusIdRanges: Array<Excel.Range> = [];

        _.forEach(notUpdatedRowIndexes, (i) => {
          const statusRange: Excel.Range = sheet.getRange(
            `${UploadStatusCellName}${i + CommonConfiguration.dataStartingRowIndex}:${UploadStatusCellName}${
              i + CommonConfiguration.dataStartingRowIndex
            }`
          );
          //let updatedRow: Excel.TableRow = existingRequestsTable.rows.getItemAt(i + 2);
          uploadStatusRanges.push(statusRange);
          statusRange.load("values");
          const statusIdRange: Excel.Range = sheet.getRange(
            `${UploadStatusIdCellName}${i + CommonConfiguration.dataStartingRowIndex}:${UploadStatusIdCellName}${
              i + CommonConfiguration.dataStartingRowIndex
            }`
          );
          uploadStatusIdRanges.push(statusIdRange);
          statusIdRange.load("values");
          updatedRowAddressIndex.push(i + CommonConfiguration.dataStartingRowIndex);
        });
        await ExcelHelper.tryCatchContextSync(context, "load the updated row values");
        let i = 0;
        uploadStatusIdRanges.forEach((updatedRow: Excel.Range) => {
          let updatedStatus = updatedRow.values.at(0)?.[CommonColumn.status]?.toString();
          if (updatedStatus !== UploadStatus.Uploaded) {
            notUpdatedRowAddressIndex.push(updatedRowAddressIndex[i]);
          }
          i++;
        });
        const hashModifiedRowIndexes: Array<number> = await ExcelUtilityHelper.updateHashValues(
          notUpdatedRowAddressIndex,
          sheet,
          context,
          props.configuration
        );

        if (!_.isEmpty(hashModifiedRowIndexes)) {
          let hashModifiedAdjustedIndexes: Array<number> = new Array<number>(); // -1 so that colorRowAfterOffline adds +2
          let uploadStatusRanges: Array<Excel.Range> = [];
          let uploadStatusIdRanges: Array<Excel.Range> = [];
          _.forEach(hashModifiedRowIndexes, (modifiedRowIndex) => {
            ExcelUtilityHelper.makeMarkForUpdateCellDirty(modifiedRowIndex + 1, sheet, MarkForUpdateCellName);
            const statusRange: Excel.Range = sheet.getRange(
              `${UploadStatusCellName}${modifiedRowIndex + 1}:${UploadStatusCellName}${modifiedRowIndex + 1}`
            );
            uploadStatusRanges.push(statusRange);
            statusRange.load("values");
            const statusIdRange: Excel.Range = sheet.getRange(
              `${UploadStatusIdCellName}${modifiedRowIndex + 1}:${UploadStatusIdCellName}${modifiedRowIndex + 1}`
            );
            uploadStatusIdRanges.push(statusIdRange);
            statusIdRange.load("values");
            hashModifiedAdjustedIndexes.push(modifiedRowIndex - 2);
          });
          await ExcelHelper.tryCatchContextSync(context, `load marked for update = false column values after offline`);
          ExcelUtilityHelper.updateStatusColumn(uploadStatusRanges, uploadStatusIdRanges, UploadStatus.Pending);
          await colorRowAfterOffline(
            context,
            hashModifiedAdjustedIndexes as number[],
            sheet,
            totalDataRowCount + 1,
            ExcelColorValue.Yellow
          );
          await ExcelHelper.tryCatchContextSync(
            context,
            `scanned and marked hash values to true for manual edits with update = false during offline`
          );
        }
      }
    }, "prepare rendering requests worksheet after offline").finally(async function () {
      await ExcelHelper.tryCatchRunAsync(async (context) => {
        context.runtime.enableEvents = true; // enable the event back
        await ExcelHelper.tryCatchContextSync(context, `enable events`);
      }, "enable events back after prepareRenderingAfterAddInOffline");
    });

    /**
     * Colors row.
     * @param rowIndexes zero-based index including header row. i.e. 0 - header Row; 1 - first Table Body Row.
     * @param sheet sheet.
     * @param totalDataRowCount non-zero-based-header-included address row count. i.e. A5 = 5th row in the excel
     * @param color defaults to empty
     */
    async function colorRowAfterOffline(
      context: Excel.RequestContext,
      rowIndexes: number[],
      sheet: Excel.Worksheet,
      totalDataRowCount: number,
      color: string = ""
    ) {
      let uploadStatusAddressRanges: Array<string> = [];
      rowIndexes.forEach((rowIndex) => {
        uploadStatusAddressRanges.push(
          `${CommonColumn.BeginningColumn}${
            rowIndex + CommonConfiguration.dataStartingRowIndex
          }:${EndingDataColumnCellName}${rowIndex + CommonConfiguration.dataStartingRowIndex}`
        );
      });

      if (!_.isEmpty(uploadStatusAddressRanges)) {
        //Batching as getRanges method can not work with large range in single call to excel.
        const batchingRangeSize = 500; // Adjust this value as needed

        for (let i = 0; i < uploadStatusAddressRanges.length; i += batchingRangeSize) {
          const range = uploadStatusAddressRanges.slice(i, i + batchingRangeSize);
          const statusRangeAreas = sheet.getRanges(range.join(", "));

          if (_.isEmpty(color)) {
            statusRangeAreas.format.fill.clear();
            ExcelUtilityHelper.defaultFormatRequestTableColors(
              sheet,
              CommonConfiguration.dataStartingRowIndex,
              totalDataRowCount,
              props.configuration
            );
          } else {
            statusRangeAreas.format.fill.color = color;
          }
        }
      }
    }
  }

  const getSplitedAddressRange = (addressRanges: string): Record<addressRanges, string> => {
    const splitedArray: string[] = addressRanges.split(":", 2);
    return {
      StartRange: splitedArray[0],
      EndRange: splitedArray[1],
    };
  };

  async function protectSheetNoRows(activeSheet: Excel.Worksheet, configuration: EntityConfiguration) {
    configuration.columns.forEach((column) => {
      const cellName = CellIndexMapping[column.CellDisplayIndex];
      activeSheet.getRange(`${cellName}${CommonConfiguration.dataStartingRowIndex}`).format.fill.color =
        ExcelColorValue.White;
      activeSheet.getRange(`${cellName}${CommonConfiguration.dataStartingRowIndex}`).format.protection.locked = true;
    });
  }

  async function protectNonEditables(
    context: Excel.RequestContext,
    tableName: string,
    noOfRecords: number,
    requestsModel: ServiceDataModel
  ) {
    let activeSheet = context.workbook.worksheets.getItem(props.activeSheetId);

    ExcelUtilityHelper.defaultFormatRequestTableColors(
      activeSheet,
      CommonConfiguration.dataStartingRowIndex,
      noOfRecords,
      props.configuration
    );

    await props.configuration.uiExcelInterface.applyFormattingOnLoad(
      activeSheet,
      CommonConfiguration.dataStartingRowIndex,
      noOfRecords,
      props.configuration,
      requestsModel
    );

    await ExcelHelper.tryCatchContextSync(context, `unprotect editable & color non-editables columns ${tableName}`);
  }

  // After upload hide hidden columns again.
  const resetHiddenColumns = React.useCallback(
    (activeSheet: Excel.Worksheet) => {
      props.configuration.columns.forEach((column) => {
        const cellName = CellIndexMapping[column.CellDisplayIndex];
        if (column.CellFormat.columnHidden) {
          activeSheet.getRange(`${cellName}:${cellName}`).columnHidden = column.CellFormat.columnHidden;
        }
      });
    },
    [props.configuration.columns]
  );
  return (
    <StyledExcelTabledSection className="doc-table-render">
      {getProgressInfo.canShowProgress ? (
        <ProgressLoader show variant="default">
          <section className="loading-msg"> {Utility.createRichTextElementName(getProgressInfo.message)} </section>
        </ProgressLoader>
      ) : (
        <>
          <section className="label-val-div-btn-wrapper">
            <section className="label-value-wrap">
              <section className="eng-label" title={labels.canvasUpdatesOn}>
                {labels.canvasUpdatesOn}
              </section>
              <section className="eng-val motif-body2-default-light" title={getUploadedOn}>
                {getUploadedOn}
              </section>
              {isUpdateInProgress && (
                <section className="error-wrapper">
                  <ErrorMessage message={labels.selectRecordsToUpdate} />
                </section>
              )}
              <SelectDropdownLayout
                key={selectedCellAddress}
                multiple={true}
                selectedCellAddress={selectedCellAddress}
                configuration={props.configuration}
                callBack={dataChangeInCellOnMultiSelect}
              />
            </section>
            <section className="update-canvas-btn-section">
              <section className="update-btn-error-wrap">
                <section className="update-btn">
                  <MotifButton
                    className={"btn"}
                    onClick={onUploadClick}
                    size="small"
                    type="button"
                    variant="primary-alt"
                    title={labels.updateCanvas}
                  >
                    <span className="update-canvas-label">{labels.updateCanvas}</span>
                  </MotifButton>
                </section>
              </section>
              {props.configuration?.entityConfiguration?.showAtlasLink != false && (
                <section className="atlas-infoText-wrapper">
                  <AtlasSummary />
                  {/* <InformationalText /> */}
                </section>
              )}
            </section>
          </section>
        </>
      )}
      {getErrorInfo.canShowError && (
        <ErrorSummary
          message={getErrorInfo.message}
          callBack={() => {
            setErrorInfo({ canShowError: false, message: `` });
          }}
        ></ErrorSummary>
      )}
    </StyledExcelTabledSection>
  );
}

export default ExcelTableRenderer;

const StyledExcelTabledSection = styled.section`
  display: flex;
  flex-direction: column;
  flex: 1;

  .motif-progress-loader {
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 61vh;
    width: 100%;
    .loading-msg {
      color: var(--neutrals-900);
    }
  }
  .label-val-div-btn-wrapper {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
  }

  .label-value-wrap {
    min-height: var(--px-122);
    padding: 0 var(--spacing-x-large) var(--spacing-x-large) var(--spacing-x-large);
    display: flex;
    flex-direction: column;
    .eng-label {
      font-size: var(--px-12);
      font-weight: 700;
      color: var(--neutrals-900);
    }
    .eng-val {
      font-size: var(--px-12);
      color: var(--neutrals-900);
    }

    .error-wrapper {
      margin-top: var(--spacing-xx-small);
      margin-bottom: var(--spacing-medium);
    }
  }

  .update-canvas-btn-section {
    border-top: 1px solid var(--neutrals-100);
    .update-btn-error-wrap {
      padding: var(--spacing-medium) var(--spacing-xx-large);
      .btn {
        margin-bottom: var(--px-10);
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
        // display: inline-block;
        min-width: unset;
        .update-canvas-label {
          width: 100%;
          min-width: unset;
          text-overflow: ellipsis;
          overflow: hidden;
          white-space: nowrap;
          display: inline-block;
        }
      }
      .update-btn {
        display: flex;
        justify-content: flex-end;
      }
      .error-wrapper {
        height: var(--px-40);
        display: flex;
        justify-content: end;
      }
    }
  }

  .atlas-infoText-wrapper {
    padding: var(--spacing-medium) var(--spacing-x-large);
    background-color: var(--neutrals-bg-gradient1);
    display: flex;
    align-items: center;
  }
`;
